/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-shadow */
import BTM from '@i18n-ecom/btm-web-sdk';
import { BtmExposeInfo, EventMap, TeaParams } from '@i18n-ecom/btm-web-sdk/dist/types/src/type/btm-core';
import { BehaviorParams, BehaviorType } from '@i18n-ecom/fe-monitor/dist/types/type/behavior';

import FeMonitor from '@i18n-ecom/fe-monitor';
import replayRender, {
  saveExternalBlackBoxRecording,
  SessionReplayProps,
  useGetExternalRecordingInfo
} from '@i18n-ecom/session-replay';
import btmCopywriting from '@i18nspect/tranquil';
import { APP_NAME_TO_BYTEIO_APP_ID } from './constants';
import { IS_MOCK, SESSION_REPLAY_MOCK_CONFIG } from './constants/mock';
import { Logger } from './logger';
import { GecBtmInitParams, IManualReportType, IRegion } from './types';
import { getBTMRemoteConfig, getFeConfig, getSessionReplayRemoteConfig, getTrajectory, sleep } from './utils';

const ValidRegionList = ['sg', 'gb', 'id', 'my', 'ph', 'th', 'us', 'vn'];
const defaultCopyWritingDelay = 5000;

export class GecBTMCore {
  private _btm = BTM;
  private _region: IRegion = 'sg';
  private _devLog = false;
  private logger: Logger | null = null;
  private initLock = false;
  private _feMonitor = FeMonitor;
  private logQueue: {
    options?: IManualReportType;
    btmBId?: string;
    type: 'click' | 'expose' | 'pageShow' | 'disappear';
    ts: number;
    useBeacon?: boolean;
  }[] = [];

  async init(params: GecBtmInitParams) {
    const { region = 'sg', devLog = false, copyWritingConfig, sessionReplayConfig, feMonitorConfig } = params;
    if (this.initLock) {
      return;
    }
    this.initLock = true;
    this._region = region;
    this.logger = new Logger(devLog);
    const BTMConfig = await getBTMRemoteConfig(this._region, params.appName, this.logger);
    this.logger.info('remote BTM config', BTMConfig);
    this._btm.init(
      { ...params },
      BTMConfig,
      btmCopywriting.getReverseIndex,
      APP_NAME_TO_BYTEIO_APP_ID[params.appName ?? '']
    );
    this.flushQueue();

    const enableCopyWriting =
      BTMConfig?.copyWritingConfig?.enableCopyWriting || copyWritingConfig?.enableCopyWriting;

    if (enableCopyWriting) {
      btmCopywriting.config({
        btmInstance: this,
        teaCollectEvent: (name: string, params: Record<string, any>) => {
          this._btm.tea?.report({
            eventName: name,
            eventParams: params
          });
        },
        copyWritingConfig: BTMConfig ? BTMConfig?.copyWritingConfig : copyWritingConfig,
        customValidation: copyWritingConfig?.customValidation,
      });
      setTimeout(() => {
        btmCopywriting.start();
      }, copyWritingConfig?.copyWritingDelay || defaultCopyWritingDelay);
    }

    await sleep(1000); // 延迟 1s 再开始 sessionReplay 的初始化
    let SessionReplayRemoteConfig = await getSessionReplayRemoteConfig(
      this._region,
      params.appName,
      this.logger
    );
    if (IS_MOCK) {
      SessionReplayRemoteConfig = SESSION_REPLAY_MOCK_CONFIG;
    }
    if (SessionReplayRemoteConfig?.enableSessionReplay) {
      this.logger.info('[SR] remote Session Replay config', SessionReplayRemoteConfig);
    }
    if (SessionReplayRemoteConfig?.enableSessionReplay && sessionReplayConfig?.sessionReplayUid) {
      const experiments = SessionReplayRemoteConfig?.experiments?.map((item) => {
        item.region = [region];
        return item;
      });
      if (experiments) {
        const enableOncall = SessionReplayRemoteConfig?.enableOncall;
        const sessionReplayProps: SessionReplayProps = {
          sessionReplayUserResearchConfigFromBackend: {
            enableSessionReplay: true,
            experiments
          },
          mode: enableOncall ? ['user-research', 'oncall'] : ['user-research'],
          sessionReplayUserCountry: region,
          btm: this,
          teaCollectEvent: (name: string, params: Record<string, any>) => {
            this._btm.tea?.report({
              eventName: name,
              eventParams: params
            });
          },
          teaCallbackOn: (this._btm as any)?.tea?.on,
          teaCallbackOff: (this._btm as any)?.tea?.off,
          appId: APP_NAME_TO_BYTEIO_APP_ID[params.appName ?? ''] ?? 4068,
          teaSessionId: (this._btm.tea?.tea as any)?.sessionManager?.getSessionId?.() || '',
          ...sessionReplayConfig
        };
        this.logger.info('sessionReplayProps config', sessionReplayProps);
        // init session replay SDK
        replayRender(sessionReplayProps);
      }
    }
    // 需要在sessionreplay初始化完成后再初始化feMonitor
    // 因为feMonitor需要获取sessionreplay的实例
    if (feMonitorConfig?.enableFeMonitor) {
      try {
        // 获取路径
        const feMonitorRemoteConfig = await getTrajectory(this._region, params.appName, this.logger);
        // 获取前端配置
        const feTrajectoryConfig = await getFeConfig(this._region, params.appName, this.logger);
        BTMConfig.journeyConfig = BTMConfig.journeyConfig ?? {};
        BTMConfig.journeyConfig.path = feMonitorRemoteConfig;
        BTMConfig.feConfig = feTrajectoryConfig;

        this._feMonitor.init({
          region: this._region,
          core: this._btm,
          feMonitorConfig,
          remoteConfig: BTMConfig
        });
        this._btm.on('logSent', (data: { action_btm: string; ts: number }) => {
          // 传入的 btm 点位到前端监控实例中
          this._feMonitor?.setCurrentNode({
            action_btm: data.action_btm,
            ts: data.ts
          });
        });
      } catch (e) {
        this.logger.error('[FeMonitor] init error', e);
      }
    }
  }

  pageInit = () => this._btm.pageInit();
  getSessionReplayRecordingInfo = useGetExternalRecordingInfo;
  saveSessionReplayBlackBoxRecording = saveExternalBlackBoxRecording;
  setPageParams = (pageParams: Record<string, any>) => this._btm.setPageParams(pageParams);
  setUnitParams = (unitParams: Record<string, any>) => this._btm.setUnitParams(unitParams);
  getPageBtmInfo = (): BtmExposeInfo => this._btm.getPageBtmInfo();
  getFullBtmInfo = (target?: HTMLElement | null, cdInfo?: string): BtmExposeInfo =>
    this._btm.getFullBtmInfo(target, cdInfo);
  sendBtmLog = (teaParams: TeaParams, target: HTMLElement | null) => this._btm.sendBtmLog(teaParams, target);
  getPageBIdByUrl = (url: string) => this._btm.getPageBIdByUrl(url);
  getAId = () => this._btm.getAId();
  getCurrentPageBId = () => this._btm.getCurrentPageBId();
  getCurrentTarget = () => this._btm.getCurrentTarget();
  setReportCallback = (callback: () => void) => this._btm.setReportCallback(callback);
  autoBizStartLog = (url: string) => this._btm.autoBizStartLog(url);
  setBCMContext = (params: Record<string, unknown>) => this._btm.setBCMContext(params);
  getBtmUrl = (
    url: string,
    isBlank: boolean,
    target: HTMLElement | null,
    unitParams?: Record<string, any> | null
  ) => this._btm.getBtmUrl(url, isBlank, target, unitParams);
  btmJump = (
    url: string,
    target: HTMLElement | null,
    opts?: {
      isReplace?: boolean;
      isBlank?: boolean;
    }
  ) => this._btm.btmJump(url, target, opts);
  on = <K extends keyof EventMap>(eventName: K, handler: EventMap[K]): void => {
    this._btm.on(eventName, handler);
  };

  feEvent = (eventName: BehaviorType, callback: (data: BehaviorParams) => void) => {
    console.log('_feMonitor event', eventName);
    this._feMonitor?.on(eventName, callback);
  };
  sendBtmClickLog = (options: IManualReportType) => {
    if (!this._btm.isInited) {
      this.logQueue.push({
        type: 'click',
        ts: Date.now(),
        options
      });
      return;
    }
    return this._btm.sendBtmLogManually({ ...options, type: 'click' });
  };

  sendBtmExposeLog = (options: IManualReportType) => {
    if (!this._btm.isInited) {
      this.logQueue.push({
        type: 'expose',
        ts: Date.now(),
        options
      });
      return;
    }
    return this._btm.sendBtmLogManually({ ...options, type: 'expose' });
  };

  sendBtmDisappearLog = (options: IManualReportType) => {
    if (!this._btm.isInited) {
      this.logQueue.push({
        type: 'disappear',
        ts: Date.now(),
        options
      });
      return;
    }
    return this._btm.sendBtmLogManually({ ...options, type: 'disappear' });
  };

  manualPageInit = (btmBId: string, params?: Record<string, unknown>, useBeacon?: boolean) => {
    const paramsObj = {
      type: 'pageShow' as const,
      ts: Date.now(),
      btmBId,
      options: {
        target: null,
        params
      },
      params,
      useBeacon
    };
    if (!this._btm.isInited) {
      this.logQueue.push(paramsObj);
      return;
    }
    return this._btm.sendBtmLogManually(paramsObj);
  };

  updateInitParams(key: 'region' | 'uniqueId', value: string) {
    if (key === 'region' && ValidRegionList.includes(key)) {
      this._region = value as IRegion;
    } else if (key === 'uniqueId') {
      this._btm.updateUniqueId(value);
    }
  }

  private flushQueue() {
    let curLog = this.logQueue.shift();
    while (curLog) {
      const { options, type, btmBId, ts, useBeacon } = curLog;
      this._btm.sendBtmLogManually({ ...options, type, btmBId, ts, useBeacon });
      curLog = this.logQueue.shift();
    }
  }
}
